"use client";

import Link from "next/link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, Sparkles } from "lucide-react";

const supplierPlans = [
  {
    name: "Supplier Starter",
    subtitle: "For small traders and first-time sellers",
    price: "₹5,000 / month",
    features: [
      "Up to 2 team seats",
      "Up to 50 active listings",
      "50 RFQ lead responses each month",
      "Basic analytics and standard placement",
    ],
  },
  {
    name: "Supplier Growth",
    subtitle: "For established exporters and manufacturers",
    price: "₹25,000 / month",
    featured: true,
    features: [
      "Up to 10 team seats",
      "Up to 500 active listings",
      "250 RFQ lead responses each month",
      "Priority placement and verified badge",
    ],
  },
  {
    name: "Supplier Enterprise",
    subtitle: "For large manufacturers and trading houses",
    price: "Custom",
    features: [
      "Unlimited seats and listings",
      "Unlimited or contracted lead volume",
      "Highest placement and advanced analytics",
      "API access and dedicated account manager",
    ],
  },
];

const buyerPlans = [
  {
    name: "Buyer Basic",
    subtitle: "For small sourcing teams",
    price: "Free",
    features: [
      "Up to 5 buyer seats",
      "25 public RFQs each month",
      "Reveal 5 supplier contacts each month",
      "Basic quote comparison",
      "Save and shortlist suppliers",
    ],
  },
  {
    name: "Buyer Growth",
    subtitle: "For structured procurement teams",
    price: "₹7,500 / month",
    featured: true,
    features: [
      "Up to 20 buyer seats",
      "250 RFQs and 50 private RFQs each month",
      "Reveal 50 supplier contacts each month",
      "Advanced comparisons and approval workflows",
      "Spend visibility and exportable reports",
    ],
  },
  {
    name: "Buyer Enterprise",
    subtitle: "For large procurement organizations",
    price: "Custom",
    features: [
      "Unlimited seats and RFQs",
      "Unlimited supplier contact reveals",
      "Private procurement at scale",
      "Advanced analytics and API access",
      "Dedicated account manager",
    ],
  },
];

const PlanGrid = ({ title, description, plans }: { title: string; description: string; plans: typeof supplierPlans }) => (
  <section className="space-y-4">
    <div className="text-center">
      <h2 className="text-2xl font-semibold text-foreground">{title}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{description}</p>
    </div>
    <div className="grid gap-4 md:grid-cols-3">
      {plans.map((plan) => (
        <Card key={plan.name} className={`border-border shadow-sm ${plan.featured ? "ring-2 ring-teal-500/30" : ""}`}>
          <CardHeader>
            <div className="flex items-center justify-between"><CardTitle className="text-xl text-foreground">{plan.name}</CardTitle>{plan.featured ? <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-100">Popular</Badge> : null}</div>
            <p className="text-sm text-muted-foreground">{plan.subtitle}</p>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="text-3xl font-semibold tracking-tight text-foreground">{plan.price}</div>
            <ul className="space-y-2 text-sm text-muted-foreground">{plan.features.map((feature) => <li key={feature} className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-teal-600" /><span>{feature}</span></li>)}</ul>
            <Button className="w-full" variant={plan.featured ? "default" : "outline"} asChild><Link href="/welcome/contact"><Sparkles className="mr-2 h-4 w-4" />Talk To Sales</Link></Button>
          </CardContent>
        </Card>
      ))}
    </div>
  </section>
);

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-muted/5 py-14">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="mx-auto max-w-6xl space-y-10">
          <div className="space-y-4 text-center">
            <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-100">
              Pricing
            </Badge>
            <h1 className="text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
              Plans For Trade Teams At Every Stage
            </h1>
            <p className="mx-auto max-w-3xl text-base leading-7 text-muted-foreground sm:text-lg">
              Choose the operating layer that matches your sourcing volume,
              supplier network, and intelligence requirements.
            </p>
          </div>

          <PlanGrid title="Buyer plans" description="Sourcing stays accessible; paid tiers add procurement controls and scale." plans={buyerPlans} />
          <PlanGrid title="Supplier plans" description="Seller pricing scales with catalog size, lead usage, and marketplace visibility." plans={supplierPlans} />

          <Card className="border-border shadow-sm">
            <CardContent className="flex flex-col items-center justify-between gap-4 p-6 text-center sm:flex-row sm:text-left">
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  Need a custom commercial model?
                </h2>
                <p className="text-sm text-muted-foreground">
                  We support phased rollout and custom packaging for enterprise
                  import-export teams.
                </p>
              </div>
              <Button asChild>
                <Link href="/welcome/contact">Request Pricing Consultation</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
